import * as React from 'react';
import {useEffect, useRef} from 'react';
import {onRender} from '@sail/engine/deprecated';
import type {Intent} from '@sail/engine';
import {focusWithoutScrolling} from '@sail/react-aria';
import type {FocusableElement, PressEvent} from '@react-types/shared';
import * as Theme from '@sail/theme-classic';
import {usePress} from '../../components/hooks/usePress';
import {
  Unstable_ActionHrefAdapterContext,
  Unstable_ActionNavigateContext,
  PREFETCH_EVENT,
  NAVIGATE_EVENT,
} from '../../components/Action';
import type {NavigateHandler} from '../../components/Action';
import {view} from '../view';
import {css} from '../css';
import type {PressEvents} from '../../components/types';

export const styles = css({
  marginBottom: css.value(0),
  marginLeft: css.value(0),
  marginRight: css.value(0),
  marginTop: css.value(0),
  paddingBottom: css.value(0),
  paddingLeft: css.value(0),
  paddingRight: css.value(0),
  paddingTop: css.value(0),
  color: css.value('inherit'),
  fontWeight: css.value('inherit'),
  fontSize: css.value('inherit'),
  fontFamily: css.value('inherit'),
  fontStyle: css.value('inherit'),
  textAlign: css.value('inherit'),
  textDecoration: css.value('none'),
  backgroundColor: css.value('transparent'),
  border: css.value(0),
  outline: css.value('none'),
  cursor: css.value('pointer'),
  '&:hover': {
    color: css.value('inherit'),
  },
});

type HrefType = 'internal' | 'external' | 'mailto' | 'empty';

function getHrefType(href: string | void): HrefType {
  if (!href) {
    return 'empty';
  }
  if (href.startsWith('mailto:')) {
    return 'mailto';
  }
  const hasScheme = !!href.match(/^\w+:\/\//);
  return !hasScheme ||
    (typeof window !== 'undefined' &&
      href.indexOf(window.location.origin) === 0)
    ? 'internal'
    : 'external';
}

interface EventKeys {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

// Unfortunately, `usePress()` doesn't pass along the original event
// for us to cancel when we handle navigation client-side.
// Instead, our strategy is to keep track of the active event target
// if navigation is handled client-side and match it up with the event target
// in our own click handler to signal we should cancel native browser navigation.
let activeEventTarget: EventTarget | null = null;
let activeKeyboardPress: boolean | null = null;

function shouldUseClientRouter({
  download,
  href,
  target,
}: {
  download?: boolean;
  href?: string;
  target?: string;
}): boolean {
  return getHrefType(href) === 'internal' && target !== '_blank' && !download;
}

export type SailNavigationEventType =
  | typeof PREFETCH_EVENT
  | typeof NAVIGATE_EVENT;

export function useClientRouting(
  handlers: {
    /**
     * Fires on press start of internal links.
     * This is where you should handle prefetching any data for the request.
     */
    onPrefetch?: (href: string, event: Event) => void;

    /**
     * Fires on press of internal links.
     * This is where you should call your router's navigation method.
     *
     * Returning `false` signals your router will not handle navigation
     * and the browser should proceed handling navigation natively.
     */
    onNavigate?: NavigateHandler<Event>;
  },
  options: {
    /**
     * A ref containing the node to which listeners for navigation events should be attached.
     * If not provided, event listeners will be attached to `window` by default.
     */
    target?: React.RefObject<EventTarget>;
  } = {},
): void {
  const onPrefetch = useRef<(typeof handlers)['onPrefetch']>(
    handlers.onPrefetch,
  );
  const onNavigate = useRef<(typeof handlers)['onNavigate']>(
    handlers.onNavigate,
  );

  useEffect(() => {
    onPrefetch.current = handlers.onPrefetch;
    onNavigate.current = handlers.onNavigate;
  });

  useEffect(() => {
    // user passed a target ref, but it hasn't been initialized yet so return early
    if (options.target && options.target.current === undefined) {
      return;
    }

    const target = options.target?.current ?? window;

    const prefetchHandler = (e: Event) => {
      const href = (e.target as HTMLAnchorElement)?.href;
      if (href) {
        onPrefetch.current?.(href, e);
      }
    };

    const navigateHandler = (e: Event) => {
      const href = (e.target as HTMLAnchorElement)?.href;
      if (href) {
        const didNavigate = onNavigate.current?.(href, e);
        if (didNavigate !== false) {
          e.preventDefault();
        }
      }
    };

    target.addEventListener(PREFETCH_EVENT, prefetchHandler);
    target.addEventListener(NAVIGATE_EVENT, navigateHandler);
    return () => {
      target.removeEventListener(PREFETCH_EVENT, prefetchHandler);
      target.removeEventListener(NAVIGATE_EVENT, navigateHandler);
    };
  }, [options.target, onPrefetch, onNavigate]);
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ActionOverrideProps {}

type OverrideProps<K, V> = Omit<K, keyof V> & V;

// It enables the href type to be overriden by the consumer
type Href = OverrideProps<{href?: string}, ActionOverrideProps>['href'];

export type ActionProps = PressEvents & {
  /**
   * Whether the action is disabled.
   * @external
   */
  disabled?: boolean;
  /**
   * Native `href` attribute
   * @type string
   * @external
   *
   */
  href?: Href;
  /**
   * @deprecated Use onPress instead of onClick
   * @ignore
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Whether the action should receive events forwarded from a specific ancestor.
   */
  receiver?: boolean;
};

function isModifiedEvent(event: EventKeys): boolean {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

// We always use an `a` tag for actions instead of a `button` or `input` tag
// because some browsers cannot render `button` or `input` inline.
export const Action = view.a.extend<ActionProps>(function Action(
  {
    disabled = false,
    onClick: onClickProp,
    onPress: onPressProp,
    onPressStart: onPressStartProp,
    onPressEnd,
    onPressChange,
    onPressUp,
    receiver,
    ...props
  },
  View,
) {
  let onPress = onPressProp;
  let onPressStart = onPressStartProp;
  let onClick = onClickProp;

  const useHrefAdapter = React.useContext(Unstable_ActionHrefAdapterContext);
  const useNavigate = React.useContext(Unstable_ActionNavigateContext);

  const hrefAdapter = useHrefAdapter();
  const navigate = useNavigate();

  props.href = hrefAdapter(props.href);

  if (!props.tabIndex) {
    props.tabIndex = 0;
  }

  // https://www.scottohara.me/note/2019/07/17/placeholder-link.html
  if (disabled) {
    delete props.href;
  }

  // To avoid doing a casting a TS control flow analysis won't be modifying optional props keys
  const href = props.href;

  // Configure HTML attributes for a link with an href
  if (href) {
    // Prevent usage of unsafe target='_blank' by setting rel="noopener noreferrer".
    // This could be more nuanced (e.g. only external links) in the future.
    // https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
    if (props.target === '_blank' && props.rel === undefined) {
      props.rel = 'noopener noreferrer';
    }

    // If this href supports client-side navigation, we add custom events.
    if (shouldUseClientRouter(props)) {
      const performNavigation = (e: PressEvent) => {
        const cancelable = !isModifiedEvent(e) ? navigate(href, e) : undefined;

        if (cancelable === false) {
          activeEventTarget = e.target;
        }
      };

      onPressStart = (e) => {
        const prefetchEvent = new CustomEvent(PREFETCH_EVENT, {
          bubbles: true,
          cancelable: true,
        });
        e.target.dispatchEvent(prefetchEvent);
        onPressStartProp && onPressStartProp(e);

        activeKeyboardPress = e.pointerType === 'keyboard';
        activeKeyboardPress && performNavigation(e);
      };
      onPress = (e) => {
        !activeKeyboardPress && performNavigation(e);

        onPressProp && onPressProp(e);
      };
      onClick = (e) => {
        if (e.button === 0 && activeEventTarget === e.currentTarget) {
          e.preventDefault();
        }

        activeEventTarget = null;

        onClickProp && onClickProp(e);
      };
    }
  } else {
    // When an href isn’t provided, we set `role="button"` to comply with
    // ARIA best practices.
    //
    // For more information, see:
    // - https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/button/button.html
    // - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
    props.role = props.role || 'button';
    delete props.href;

    // If this is a button, we need to add keyboard events:
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#Keyboard_interactions
    // https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/button/button.html
    props.onKeyPress = function buttonKeyboardBehavior(
      e: React.KeyboardEvent<HTMLAnchorElement>,
    ) {
      if (e.key === 'Enter' || e.key === ' ') {
        // action(e, null);
        e.preventDefault();
      }
    };

    // If this is a button, we also need to ensure focus is placed onto the button when clicked (unlike with an anchor)
    // https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-3
    onPressStart = (e) => {
      focusWithoutScrolling(e.target as FocusableElement);
      onPressStartProp && onPressStartProp(e);
    };
  }

  const ref = props.ref ?? React.createRef<HTMLAnchorElement>();
  const {pressProps, isPressed} = usePress({
    isDisabled: disabled,
    onPress,
    onPressStart,
    onPressEnd,
    onPressUp,
    onPressChange,
    preventFocusOnPress: true,
    ref: ref as React.RefObject<HTMLElement>,
  });

  return (
    <View
      {...props}
      data-receiver={receiver}
      {...(disabled ? {'aria-disabled': true, tabIndex: -1} : {})}
      ref={ref}
      uses={[
        pressProps,
        onClick ? {onClick} : null,
        styles,
        Theme.ActionVariants.assign({disabled, pressed: isPressed}),
        Theme.focus,
      ]}
    />
  );
});

const FORWARDED_PRESS_EVENTS = [
  'keydown',
  'keyup',
  'click',
  'pointerup',
  'pointerdown',
  'pointermove',
  'pointercancel',
  'dragstart',
  'dragdown',
  'mouseenter',
  'mouseleave',
  'mouseup',
  'mousedown',
  'touchstart',
  'touchend',
  'touchmove',
  'touchcancel',
];

export function forwardPress(): Intent<{ref: React.RefObject<HTMLElement>}> {
  return onRender(() => {
    const ref = useRef<HTMLElement>(null);

    useEffect(() => {
      FORWARDED_PRESS_EVENTS.forEach((event) => {
        if (!ref.current) {
          return null;
        }

        const anchor = ref.current.querySelector('a[data-receiver="true"]');

        if (anchor == null) {
          // eslint-disable-next-line no-console
          console.warn('No anchor available to handle receive events');
        } else {
          const createForwardedEvent = (e: Event) => {
            const forwardedEvent = new Event(event, e);
            forwardedEvent.stopPropagation();
            anchor.dispatchEvent(forwardedEvent);
          };

          ref.current.addEventListener(event, createForwardedEvent);

          return () =>
            ref.current?.removeEventListener(event, createForwardedEvent);
        }
      });
    }, []);

    return [{ref}];
  });
}
